import type { ProductId } from '@/config/products';
import { getPostLoginRoute, getProductEntryRoute } from '@/utils/productAccess';
import { getApiBaseUrl } from '@/utils/apiBase';
import { getOrgAdminLanding, isOrgAdminRole, ORG_HOME_PATH } from '@/utils/orgConsole';
import { UserRole } from '@/types';

/** Members/managers land here when signed in but no product seat is assigned. */
export const AWAITING_ACCESS_PATH = '/awaiting-access';

const ALLOWED_WITHOUT_LICENSE = [
  AWAITING_ACCESS_PATH,
  '/settings',
  '/notifications',
] as const;

export function isAllowedWithoutProductLicense(pathname: string): boolean {
  const path = (pathname.split('?')[0] || '/').replace(/\/$/, '') || '/';
  return ALLOWED_WITHOUT_LICENSE.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`),
  );
}

export function isOrgConsoleRole(role?: string | null): boolean {
  return role === UserRole.SUPER_ADMIN || isOrgAdminRole(role);
}

export function isDeliveryUserRole(role?: string | null): boolean {
  if (!role) return false;
  return !isOrgConsoleRole(role);
}

export function resolveAuthenticatedHome(params: {
  role?: string | null;
  settings?: Record<string, unknown> | null;
  /** `undefined`/`null` = license state unknown (API soft-fail) → legacy route */
  launchableProductIds?: ProductId[] | null;
}): string {
  const { role, settings, launchableProductIds } = params;

  // Soft-fail: unknown license state must not trap members on awaiting-access.
  if (launchableProductIds == null) {
    return getPostLoginRoute(settings, role);
  }

  if (
    isDeliveryUserRole(role) &&
    Array.isArray(launchableProductIds) &&
    launchableProductIds.length === 0
  ) {
    return AWAITING_ACCESS_PATH;
  }

  if (Array.isArray(launchableProductIds) && launchableProductIds.length > 0) {
    return getDefaultRouteForLaunchableProducts(launchableProductIds, role, settings);
  }

  return getPostLoginRoute(settings, role);
}

/** Primary landing route from the user's assigned product seats. */
export function getDefaultRouteForLaunchableProducts(
  launchable: ProductId[],
  role?: string | null,
  settings?: Record<string, unknown> | null,
): string {
  if (launchable.length === 0) return AWAITING_ACCESS_PATH;

  if (isOrgAdminRole(role)) {
    if (launchable.includes('pm')) {
      return getOrgAdminLanding(settings);
    }
    return ORG_HOME_PATH;
  }

  const priority: ProductId[] = ['pm', 'code', 'ship', 'lenc'];
  const primary = priority.find((id) => launchable.includes(id)) ?? launchable[0];

  // If member has multiple licensed products → send them to the hub picker
  // so they can choose which one to open. If only one → go straight to it.
  if (!isOrgAdminRole(role) && launchable.length > 1) {
    return '/workspace';
  }

  return getProductEntryRoute(primary, role);
}

export function userHasProductLicense(
  launchable: ProductId[] | undefined,
  productId: ProductId,
): boolean {
  return Boolean(launchable?.includes(productId));
}

/**
 * Fetch current user's launchable products after login (before RTK cache is warm).
 * Returns `null` on network/HTTP failure so callers fall back to legacy routing
 * instead of treating the failure as "no seats".
 */
export async function fetchMyProductLicenses(
  accessToken: string,
): Promise<ProductId[] | null> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/org/my-licenses`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) return null;
    const json = await res.json();
    return (json?.data?.launchableProductIds ?? []) as ProductId[];
  } catch {
    return null;
  }
}

export async function resolveAuthenticatedHomeAfterAuth(params: {
  role?: string | null;
  settings?: Record<string, unknown> | null;
  accessToken?: string;
  entryProductId?: ProductId | null;
}): Promise<string> {
  const { role, settings, accessToken, entryProductId } = params;
  let launchable: ProductId[] | null | undefined;

  if (accessToken) {
    launchable = await fetchMyProductLicenses(accessToken);
  }

  // Soft-fail / unknown → legacy getPostLoginRoute (do not trap on awaiting-access).
  if (launchable == null) {
    if (entryProductId && entryProductId !== 'pm') {
      return getPostLoginRoute(settings, role, entryProductId);
    }
    return getPostLoginRoute(settings, role);
  }

  if (
    isDeliveryUserRole(role) &&
    Array.isArray(launchable) &&
    launchable.length === 0
  ) {
    return AWAITING_ACCESS_PATH;
  }

  if (Array.isArray(launchable) && launchable.length > 0) {
    return getDefaultRouteForLaunchableProducts(launchable, role, settings);
  }

  if (entryProductId && entryProductId !== 'pm') {
    return getPostLoginRoute(settings, role, entryProductId);
  }

  return resolveAuthenticatedHome({ role, settings, launchableProductIds: launchable });
}
